import React, { useEffect, useRef, useState } from "react";
import { InternalLayout } from "@/components/InternalLayout";
import api, { API_BASE } from "@/lib/api";
import { MessageSquare, Plus, Send, Pencil, Trash2, Check, X, SlidersHorizontal } from "lucide-react";

// ask4-chat-Modul (AP-06): Multi-Turn-Chat über Studios Gateway, Token-
// Streaming für alle sechs Provider. UI ist reiner Client von /api/chat/*.
export default function Chat() {
  const [configs, setConfigs] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [configId, setConfigId] = useState("");
  const [newSystemPrompt, setNewSystemPrompt] = useState("");
  const [showNewOptions, setShowNewOptions] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [renameId, setRenameId] = useState(null);
  const [renameText, setRenameText] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [editSystem, setEditSystem] = useState(false);
  const [systemDraft, setSystemDraft] = useState("");
  const endRef = useRef(null);

  const active = conversations.find((c) => c.conversation_id === activeId) || null;

  const loadConversations = () =>
    api.get("/chat/conversations").then((r) => setConversations(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/llm-configs").then((r) => {
      setConfigs(r.data);
      if (r.data[0]) setConfigId(r.data[0].config_id);
    }).catch(() => {});
    loadConversations();
  }, []);

  useEffect(() => {
    if (!activeId) return;
    setEditSystem(false);
    api.get(`/chat/conversations/${activeId}/messages`).then((r) => setMessages(r.data)).catch(() => {});
  }, [activeId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const fail = (e, fallback) => setError(e?.response?.data?.detail || e?.message || fallback);

  const newConversation = async () => {
    if (!configId) return;
    try {
      const r = await api.post("/chat/conversations", {
        title: "Neue Unterhaltung",
        config_id: configId,
        system_prompt: newSystemPrompt.trim() || null,
      });
      setNewSystemPrompt("");
      setShowNewOptions(false);
      await loadConversations();
      setActiveId(r.data.conversation_id);
      setMessages([]);
    } catch (e) { fail(e, "Unterhaltung konnte nicht angelegt werden."); }
  };

  const saveRename = async (id) => {
    const title = renameText.trim();
    setRenameId(null);
    if (!title) return;
    try {
      await api.patch(`/chat/conversations/${id}`, { title });
      await loadConversations();
    } catch (e) { fail(e, "Umbenennen fehlgeschlagen."); }
  };

  const doDelete = async (id) => {
    setDeleteId(null);
    try {
      await api.delete(`/chat/conversations/${id}`);
      if (activeId === id) { setActiveId(null); setMessages([]); }
      await loadConversations();
    } catch (e) { fail(e, "Löschen fehlgeschlagen."); }
  };

  const saveSystemPrompt = async () => {
    try {
      await api.patch(`/chat/conversations/${activeId}`, { system_prompt: systemDraft });
      setEditSystem(false);
      await loadConversations();
    } catch (e) { fail(e, "System-Prompt konnte nicht gespeichert werden."); }
  };

  // Token-Streaming per SSE (fetch + ReadableStream — axios streamt im Browser nicht).
  const send = async () => {
    if (!input.trim() || !activeId || sending) return;
    const content = input.trim();
    setInput("");
    setError("");
    const aid = `tmp-a-${Date.now()}`;
    setMessages((m) => [
      ...m,
      { role: "user", content, message_id: `tmp-u-${Date.now()}` },
      { role: "assistant", content: "", message_id: aid, streaming: true },
    ]);
    setSending(true);
    try {
      const token = localStorage.getItem("aw_token");
      const resp = await fetch(`${API_BASE}/chat/conversations/${activeId}/messages/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ content }),
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop() || "";
        for (const ev of events) {
          const line = ev.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          let data;
          try { data = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (data.delta) {
            setMessages((m) => m.map((x) => (x.message_id === aid ? { ...x, content: x.content + data.delta } : x)));
          } else if (data.error) {
            setError(data.error);
          } else if (data.done) {
            if (data.persisted) {
              setMessages((m) => m.map((x) => (x.message_id === aid
                ? { ...x, message_id: data.message_id, provider: data.provider, model: data.model, streaming: false }
                : x)));
            } else {
              // Fehler vor dem ersten Token: leere Assistenz-Blase entfernen.
              setMessages((m) => m.filter((x) => x.message_id !== aid));
            }
          }
        }
      }
    } catch (e) {
      setError(String(e?.message || e));
      setMessages((m) => m.filter((x) => !(x.message_id === aid && !x.content)));
    } finally { setSending(false); }
  };

  return (
    <InternalLayout>
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">AppWerkstatt OS · Modul</div>
        <h1 className="font-display text-3xl text-foreground flex items-center gap-3">
          Chat <MessageSquare className="w-6 h-6 text-primary" />
        </h1>
        <p className="text-sm text-muted-foreground mt-2">Multi-Provider-Chat über das Studio-Gateway — openai, anthropic, google, deepseek, openrouter und kimi, alle mit Token-Streaming.</p>
      </div>

      <div className="flex gap-4" style={{ height: "70vh" }} data-testid="chat-page">
        {/* Sidebar */}
        <div className="w-72 shrink-0 border border-border bg-card flex flex-col">
          <div className="p-2 border-b border-border space-y-2">
            <div className="flex gap-1">
              <select value={configId} onChange={(e) => setConfigId(e.target.value)}
                className="flex-1 text-xs border border-border bg-background rounded-sm px-2 py-1.5" data-testid="chat-config-select">
                {configs.length === 0 && <option value="">Keine LLM-Config</option>}
                {configs.map((c) => <option key={c.config_id} value={c.config_id}>{c.name || `${c.provider}/${c.model}`}</option>)}
              </select>
              <button onClick={() => setShowNewOptions((v) => !v)} title="System-Prompt für neue Unterhaltung"
                className={`px-2 rounded-sm border ${showNewOptions ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
                <SlidersHorizontal className="w-3.5 h-3.5" />
              </button>
              <button onClick={newConversation} disabled={!configId} title="Neue Unterhaltung"
                className="px-2 rounded-sm bg-primary text-primary-foreground disabled:opacity-40" data-testid="chat-new">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {showNewOptions && (
              <textarea value={newSystemPrompt} onChange={(e) => setNewSystemPrompt(e.target.value)}
                placeholder="System-Prompt für die neue Unterhaltung (optional) …" rows={3}
                className="w-full text-xs border border-border bg-background rounded-sm px-2 py-1.5" data-testid="chat-new-system" />
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.map((c) => (
              <div key={c.conversation_id}
                className={`group flex items-center border-b border-border/60 ${activeId === c.conversation_id ? "bg-secondary" : "hover:bg-secondary/50"}`}>
                {renameId === c.conversation_id ? (
                  <div className="flex-1 flex items-center gap-1 px-2 py-1.5">
                    <input autoFocus value={renameText} onChange={(e) => setRenameText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveRename(c.conversation_id); if (e.key === "Escape") setRenameId(null); }}
                      className="flex-1 text-sm border border-border bg-background rounded-sm px-2 py-0.5" data-testid="chat-rename-input" />
                    <button onClick={() => saveRename(c.conversation_id)} className="text-primary"><Check className="w-4 h-4" /></button>
                    <button onClick={() => setRenameId(null)} className="text-muted-foreground"><X className="w-4 h-4" /></button>
                  </div>
                ) : deleteId === c.conversation_id ? (
                  <div className="flex-1 flex items-center justify-between px-3 py-2 text-xs">
                    <span className="text-destructive">Wirklich löschen?</span>
                    <span className="flex gap-2">
                      <button onClick={() => doDelete(c.conversation_id)} className="text-destructive underline" data-testid="chat-delete-confirm">Ja</button>
                      <button onClick={() => setDeleteId(null)} className="text-muted-foreground underline">Nein</button>
                    </span>
                  </div>
                ) : (
                  <>
                    <button onClick={() => setActiveId(c.conversation_id)}
                      className="flex-1 text-left px-3 py-2 text-sm truncate text-foreground">
                      {c.title}
                    </button>
                    <span className="hidden group-hover:flex items-center gap-1 pr-2">
                      <button title="Umbenennen" onClick={() => { setRenameId(c.conversation_id); setRenameText(c.title); }}
                        className="text-muted-foreground hover:text-foreground" data-testid="chat-rename">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button title="Löschen" onClick={() => setDeleteId(c.conversation_id)}
                        className="text-muted-foreground hover:text-destructive" data-testid="chat-delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  </>
                )}
              </div>
            ))}
            {conversations.length === 0 && <div className="p-3 text-xs text-muted-foreground">Noch keine Unterhaltungen. Wähle oben eine Config und starte mit „+".</div>}
          </div>
        </div>

        {/* Thread */}
        <div className="flex-1 border border-border bg-card flex flex-col min-w-0">
          {active && (
            <div className="border-b border-border px-4 py-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-foreground truncate">{active.title}</div>
                <div className="font-mono text-[10px] text-muted-foreground truncate">
                  {(configs.find((c) => c.config_id === active.config_id) || {}).name || active.config_id}
                </div>
              </div>
              <button
                onClick={() => { setSystemDraft(active.system_prompt || ""); setEditSystem((v) => !v); }}
                className={`shrink-0 text-xs inline-flex items-center gap-1 ${active.system_prompt ? "text-primary" : "text-muted-foreground"} hover:text-foreground`}
                data-testid="chat-system-toggle"
              >
                <SlidersHorizontal className="w-3.5 h-3.5" /> System-Prompt{active.system_prompt ? " ✓" : ""}
              </button>
            </div>
          )}
          {active && editSystem && (
            <div className="border-b border-border px-4 py-3 space-y-2 bg-secondary/40">
              <textarea value={systemDraft} onChange={(e) => setSystemDraft(e.target.value)} rows={3}
                placeholder="System-Prompt dieser Unterhaltung …"
                className="w-full text-xs border border-border bg-background rounded-sm px-2 py-1.5" data-testid="chat-system-input" />
              <div className="flex gap-2 justify-end">
                <button onClick={() => setEditSystem(false)} className="text-xs text-muted-foreground underline">Abbrechen</button>
                <button onClick={saveSystemPrompt} className="text-xs bg-primary text-primary-foreground rounded-sm px-3 py-1" data-testid="chat-system-save">Speichern</button>
              </div>
              <div className="text-[10px] text-muted-foreground">Gilt ab der nächsten Nachricht dieser Unterhaltung.</div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {!activeId && <div className="text-sm text-muted-foreground">Wähle links eine Unterhaltung oder starte eine neue.</div>}
            {messages.map((m) => (
              <div key={m.message_id} className={`max-w-[80%] px-3 py-2 rounded-md text-sm ${m.role === "user" ? "ml-auto bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                <div className="whitespace-pre-wrap">{m.content}{m.streaming && !m.content ? "…" : ""}</div>
                {m.role === "assistant" && m.model && (
                  <div className="mt-1 font-mono text-[9px] opacity-60">{m.provider}/{m.model}{m.input_tokens != null ? ` · ${m.input_tokens}/${m.output_tokens} tok` : ""}</div>
                )}
              </div>
            ))}
            {sending && <div className="text-xs text-muted-foreground">antwortet …</div>}
            <div ref={endRef} />
          </div>
          {error && (
            <div className="px-4 py-2 text-xs text-destructive border-t border-border flex items-center justify-between gap-3" data-testid="chat-error">
              <span className="truncate">Fehler: {error}</span>
              <button onClick={() => setError("")} className="underline shrink-0">Schließen</button>
            </div>
          )}
          <div className="p-2 border-t border-border flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={activeId ? "Nachricht …" : "Erst Unterhaltung starten"}
              disabled={!activeId || sending}
              className="flex-1 px-3 py-2 text-sm border border-border bg-background rounded-sm" data-testid="chat-input" />
            <button onClick={send} disabled={!activeId || sending || !input.trim()}
              className="px-4 py-2 text-sm bg-aw-coral text-white rounded-full disabled:opacity-40 flex items-center gap-1 hover:brightness-95 transition" data-testid="chat-send">
              <Send className="w-4 h-4" /> Senden
            </button>
          </div>
        </div>
      </div>
    </InternalLayout>
  );
}
